/**
 * SingingPortraitNode (Canvas V2)
 *
 * Makes the subject of a photo — usually a pet — sing a song. The backend
 * agent runs self-hosted MiniMax-H3 reference-to-video with the song as
 * reference audio; a run takes several minutes.
 *
 * Inputs:  in_image (image, required) — the photo, used as-is
 *          in_audio (audio, optional)  — a song; empty means "write one from
 *                                        in_lyrics + in_song_style"
 * Outputs: out_video (the singing clip, song as soundtrack), out_song
 *
 * Layout mirrors CaptionerNode: the result video is the thing you check, so it
 * is the body of the node. The creative inputs (lyrics, performance) are
 * editable inline; the numeric/enum settings live behind a config chip in the
 * shared FlyoutPortal, the same pattern SoundExpertConfigTab uses.
 */

import { memo, useCallback, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Position, useEdges, useReactFlow, type NodeProps } from 'reactflow'
import {
  executeNode,
  getAssetUrl,
  InsufficientSparksError,
  type SingingPortraitNodeData,
} from '@ponpon/api-client'
import { BaseNode, FlyoutPortal, VideoPreviewPlayer, useNodeActions } from '../shared'
import { TypedHandle } from '../../TypedHandle'
import { useCanvasOptional } from '../../CanvasContext'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { useShowInsufficientSparks } from '../../../../contexts/InsufficientSparksContext'
import { useClickLock } from '../../../../hooks/useClickLock'

// Schema bounds (schemas/nodes/singingPortrait.json): in_duration is 2-60 s.
// The backend renders anything over 12 s as equal segments of the same song
// and joins them (MiniMax-H3 renders at most 15 s at a time). Defaults below
// repeat the schema defaults only as a display fallback for nodes created
// before the defaults were written.
const MIN_DURATION = 2
const MAX_DURATION = 60
const DEFAULT_DURATION = 12
const ASPECT_RATIO_OPTIONS = ['9:16', '1:1', '16:9'] as const
const DEFAULT_ASPECT_RATIO = '9:16'
const MOTION_OPTIONS = ['dance', 'sing'] as const
const DEFAULT_MOTION = 'dance'

const LABEL_CLASS = 'text-xxs uppercase tracking-wider text-text-muted block mb-1.5'
const FIELD_CLASS =
  'w-full text-xs bg-surface-1 text-text-primary border border-border-subtle rounded px-2 py-1.5 focus:outline-none focus:border-primary/50 transition-colors placeholder:text-text-muted/40'

function segmentedBtnClass(isSelected: boolean): string {
  return `flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${
    isSelected
      ? 'bg-primary/20 text-primary border border-primary/40'
      : 'bg-surface-2 text-text-muted border border-border-subtle hover:text-foreground'
  }`
}

/** Clamp a user-typed duration to the node's 2-60 s window. */
export function clampDuration(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_DURATION
  return Math.min(MAX_DURATION, Math.max(MIN_DURATION, Math.round(value)))
}

const SingingPortraitNode = memo(function SingingPortraitNode(
  props: NodeProps<SingingPortraitNodeData>,
) {
  const { data, selected, id } = props
  const [hovering, setHovering] = useState(false)
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)
  const [isConfigOpen, setIsConfigOpen] = useState(false)
  const configChipRef = useRef<HTMLButtonElement>(null)

  const projectId = useProjectId()
  const canvas = useCanvasOptional()
  const { setNodes } = useReactFlow()
  const edges = useEdges()
  const { handleDownload } = useNodeActions({ nodeId: id })
  const showInsufficientSparks = useShowInsufficientSparks()
  const { isLocked, run } = useClickLock(1000)

  const status = data.status ?? 'idle'
  const isBusy =
    status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation'

  // in_image / in_audio are asset ports: they are filled by edges, never by a
  // direct write (CanvasContext.assertNoDirectAssetInputPortWrites).
  const hasPhoto = edges.some((e) => e.target === id && e.targetHandle === 'in_image')
  const hasSong = edges.some((e) => e.target === id && e.targetHandle === 'in_audio')

  const videoUrl = data.out_video && projectId ? getAssetUrl(projectId, data.out_video) : null
  const duration = data.in_duration ?? DEFAULT_DURATION
  const aspectRatio = data.in_aspect_ratio ?? DEFAULT_ASPECT_RATIO
  const motion = data.in_motion ?? DEFAULT_MOTION
  const audioStart = data.in_audio_start_s ?? 0

  const updateField = useCallback(
    (updates: Partial<SingingPortraitNodeData>) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...updates } } : n)),
      )
      canvas?.updateNodeData(id, updates)
    },
    [id, setNodes, canvas],
  )

  const setStatus = useCallback(
    (next: SingingPortraitNodeData['status'], error?: string) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, status: next, error } } : n)),
      )
    },
    [id, setNodes],
  )

  const handleGenerate = useCallback(() => {
    if (isBusy) return
    if (!projectId) {
      setStatus('error', 'Missing projectId.')
      return
    }
    void run(id, async () => {
      setStatus('running')
      try {
        await executeNode(projectId, id)
      } catch (err) {
        if (err instanceof InsufficientSparksError) {
          showInsufficientSparks({ balance: err.balance, required: err.required, nodeId: id })
          setStatus('idle')
          return
        }
        setStatus('error', String(err))
      }
    })
  }, [isBusy, projectId, id, run, setStatus, showInsufficientSparks])

  const onDownload = useCallback(() => {
    if (!data.out_video) return
    handleDownload(data.out_video, `${data.in_name || 'singing-pet'}.mp4`)
  }, [data.out_video, data.in_name, handleDownload])

  const generateDisabled = isBusy || !hasPhoto || isLocked(id)
  const songSummary = hasSong ? 'your song' : 'original song'

  return (
    <div onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}>
      <TypedHandle
        type="target"
        position={Position.Left}
        id="in_image"
        portType="image"
        showTooltip
        style={{ top: '35%' }}
      />
      <TypedHandle
        type="target"
        position={Position.Left}
        id="in_audio"
        portType="audio"
        showTooltip
        style={{ top: '65%' }}
      />

      <BaseNode
        title={data.in_name || data.out_name || 'Singing Portrait'}
        icon="🎤"
        selected={selected}
        status={status}
      >
        {videoUrl ? (
          <VideoPreviewPlayer
            videoUrl={videoUrl}
            isHovering={hovering}
            showExpand
            showDownload
            onExpand={() => setIsVideoModalOpen(true)}
            onDownload={onDownload}
          />
        ) : (
          <div
            className="flex h-24 items-center justify-center px-2 text-center text-xs text-muted-foreground"
            data-testid="singing-portrait-empty"
          >
            {isBusy
              ? 'Singing… this takes a few minutes'
              : hasPhoto
                ? 'Write lyrics (or connect a song), then Generate'
                : 'Connect a photo, then Generate'}
          </div>
        )}

        {status === 'error' && data.error && (
          <div className="mt-2 line-clamp-2 text-[10px] text-destructive">{data.error}</div>
        )}

        <div
          className="mt-2 flex flex-col gap-2 nopan nodrag nowheel"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <label className="block">
            <span className={LABEL_CLASS}>Lyrics</span>
            <textarea
              value={data.in_lyrics ?? ''}
              onChange={(e) => updateField({ in_lyrics: e.target.value })}
              placeholder={
                hasSong
                  ? 'Using the connected song'
                  : 'Two to four short lines about your pet…'
              }
              disabled={hasSong}
              rows={3}
              data-testid="singing-lyrics-input"
              className={`${FIELD_CLASS} resize-none disabled:opacity-50`}
            />
          </label>

          <label className="block">
            <span className={LABEL_CLASS}>Performance</span>
            <input
              type="text"
              value={data.in_prompt ?? ''}
              onChange={(e) => updateField({ in_prompt: e.target.value })}
              placeholder="e.g. head bobbing to the beat, sunglasses on"
              data-testid="singing-prompt-input"
              className={FIELD_CLASS}
            />
          </label>

          <div className="flex items-center gap-1.5">
            <button
              ref={configChipRef}
              type="button"
              onClick={() => setIsConfigOpen((open) => !open)}
              className="truncate rounded-full border border-border-subtle bg-surface-1 px-2 py-1 text-xxs text-text-muted transition-colors hover:bg-surface-2 hover:text-foreground"
              data-testid="singing-config-chip"
              title="Singing settings"
            >
              {duration}s · {aspectRatio} · {songSummary}
            </button>
            <div className="flex-1" />
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generateDisabled}
              className="flex h-8 w-8 items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              style={{ backgroundColor: 'var(--primary)' }}
              title={hasPhoto ? 'Generate' : 'Connect a photo first'}
              data-testid="generate-btn"
            >
              <svg
                className="h-4 w-4 text-primary-foreground"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
            </button>
          </div>
        </div>

        <FlyoutPortal
          triggerRef={configChipRef}
          isOpen={isConfigOpen}
          onClose={() => setIsConfigOpen(false)}
          minWidth={240}
        >
          <div className="flex flex-col gap-3" data-testid="singing-config-flyout">
            <div>
              <span className={LABEL_CLASS}>Aspect ratio</span>
              <div className="flex gap-1">
                {ASPECT_RATIO_OPTIONS.map((ratio) => (
                  <button
                    key={ratio}
                    type="button"
                    onClick={() => updateField({ in_aspect_ratio: ratio })}
                    className={segmentedBtnClass(aspectRatio === ratio)}
                    data-testid={`singing-aspect-${ratio}`}
                  >
                    {ratio}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <span className={LABEL_CLASS}>Motion</span>
              <div className="flex gap-1">
                {MOTION_OPTIONS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => updateField({ in_motion: option })}
                    className={segmentedBtnClass(motion === option)}
                    data-testid={`singing-motion-${option}`}
                  >
                    {option === 'dance' ? 'Sing + dance' : 'Sing'}
                  </button>
                ))}
              </div>
            </div>

            <label className="block">
              <span className={LABEL_CLASS}>
                Duration ({MIN_DURATION}-{MAX_DURATION}s)
              </span>
              <input
                type="number"
                min={MIN_DURATION}
                max={MAX_DURATION}
                step={1}
                value={duration}
                onChange={(e) => updateField({ in_duration: clampDuration(Number(e.target.value)) })}
                data-testid="singing-duration-input"
                className={FIELD_CLASS}
              />
            </label>

            <label className="block">
              <span className={LABEL_CLASS}>Song start (s)</span>
              <input
                type="number"
                min={0}
                step={0.5}
                value={audioStart}
                disabled={!hasSong}
                onChange={(e) => {
                  const next = Number(e.target.value)
                  updateField({ in_audio_start_s: Number.isFinite(next) && next > 0 ? next : 0 })
                }}
                data-testid="singing-start-input"
                className={`${FIELD_CLASS} disabled:opacity-50`}
                title={hasSong ? 'Where the clip starts in your song' : 'Only used with a connected song'}
              />
            </label>

            <label className="block">
              <span className={LABEL_CLASS}>Song style</span>
              <input
                type="text"
                value={data.in_song_style ?? ''}
                disabled={hasSong}
                onChange={(e) => updateField({ in_song_style: e.target.value })}
                placeholder="upbeat pop, bright clear vocal, catchy melody"
                data-testid="singing-style-input"
                className={`${FIELD_CLASS} disabled:opacity-50`}
              />
            </label>
          </div>
        </FlyoutPortal>
      </BaseNode>

      <TypedHandle
        type="source"
        position={Position.Right}
        id="out_video"
        portType="video"
        showTooltip
        style={{ top: '35%' }}
      />
      <TypedHandle
        type="source"
        position={Position.Right}
        id="out_song"
        portType="audio"
        showTooltip
        style={{ top: '65%' }}
      />

      {isVideoModalOpen &&
        videoUrl &&
        createPortal(
          <div
            data-testid="video-lightbox-backdrop"
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 p-8 backdrop-blur-sm"
            onClick={() => setIsVideoModalOpen(false)}
          >
            <div
              data-testid="video-lightbox"
              className="relative flex max-h-full w-full max-w-6xl items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="video-lightbox-close"
                onClick={() => setIsVideoModalOpen(false)}
                className="absolute -top-12 right-0 text-text-secondary transition-colors hover:text-foreground"
              >
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <video
                src={videoUrl}
                controls
                autoPlay
                className="max-h-[85vh] max-w-full rounded-lg bg-black shadow-2xl"
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
})

export { SingingPortraitNode }
